import { type PodcastProject, type PodcastTranscriptSegment } from "@/models/podcast";
import { PODCAST_SUPPORTED_LANGUAGES } from "./languages";

const MAX_DISCUSSION_CONTEXT_CHARS = 6000;

function compactWhitespace(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function formatPodcastScriptLine(segment: PodcastTranscriptSegment, index: number) {
  const text = compactWhitespace(segment.text || segment.originalText || "");
  if (!text) return null;
  return `${index + 1}. ${segment.speaker}: ${text}`;
}

export function podcastHasDiscussableScript(podcast: Pick<PodcastProject, "segments">) {
  return podcast.segments.some((segment) =>
    compactWhitespace(segment.text || segment.originalText || "")
  );
}

export function buildPodcastDiscussionContext(podcast: PodcastProject) {
  const scriptLines = podcast.segments
    .map(formatPodcastScriptLine)
    .filter((line): line is string => Boolean(line));

  if (scriptLines.length === 0) return "";

  const context = [
    "<podcast_discussion>",
    "Session Focus: discuss and practice the podcast script the learner just opened. Use the transcript as shared context, not as a rigid lesson script.",
    `Podcast Title: ${podcast.title}`,
    `Target Language: ${podcast.targetLanguage}`,
    podcast.targetLanguageCode ? `Target Language Code: ${podcast.targetLanguageCode}` : "",
    `CEFR Level: ${podcast.cefrLevel}`,
    `Topic: ${podcast.topic}`,
    podcast.chapterId ? `Language Journey Chapter ID: ${podcast.chapterId}` : "",
    "How to run this session:",
    "- Start by asking what part of the podcast the learner wants to discuss or practice.",
    "- Help them understand the transcript, replay meaning, pronunciation, grammar, vocabulary, and natural alternatives.",
    "- Ask short speaking questions based on the script and let the learner answer in the target language.",
    "- Keep the podcast script active as the reference point unless the learner asks to move on.",
    "Podcast Script:",
    scriptLines.join("\n"),
    "</podcast_discussion>",
  ]
    .filter(Boolean)
    .join("\n");

  return context.slice(0, MAX_DISCUSSION_CONTEXT_CHARS);
}

export function getPodcastDiscussionHref(podcast: PodcastProject) {
  const languageCode =
    podcast.chapterLanguageCode ||
    podcast.targetLanguageCode ||
    PODCAST_SUPPORTED_LANGUAGES[podcast.targetLanguage];
  const params = new URLSearchParams({
    discussPodcast: "1",
    podcastId: podcast.id,
  });

  if (languageCode) params.set("languageCode", languageCode);
  if (podcast.chapterId) {
    params.set("sessionMode", "journey");
    params.set("journeyChapterId", podcast.chapterId);
  }

  return `/?${params.toString()}`;
}
